import { ReelGridPresenter, ReelPaylineEffect, ReelSpinMechanic, buildReelsReplayPlugin } from "@slot/mechanics-reels";

import type { BloodyTeddyScene } from "../scene/createScene";
import { GRID_AREA_SIZE } from "../scene/GridContainer";
import { WIN_PRESENTATION_TUNING, SYMBOL_WIN_ROTATE_SYMBOL_IDS } from "../tuning/winPresentationTuning";
import { LAND_SHINE_TUNING, SHINE_LAND_SYMBOL_IDS, SQUASH_LAND_SYMBOL_IDS, SQUASH_LAND_TUNING } from "../tuning/symbolLandTuning";
import { resolveSymbolWinFrames } from "../tuning/symbolWinFrames";
import { BIG_WIN_TUNING } from "../tuning/bigWinTuning";
import { FEATURE_TRIGGER_TUNING } from "../tuning/featureTriggerTuning";
import { FEATURE_FINISHED_TUNING } from "../tuning/featureFinishedTuning";
import { REEL_MOTION_TUNING } from "../tuning/reelMotionTuning";
import { REEL_WIN_TUNING } from "../tuning/reelWinTuning";
import { LAND_BOUNCE_TUNING } from "../tuning/landBounceTuning";
import { ANTICIPATION_BACKGROUND_TUNING } from "../tuning/anticipationBackgroundTuning";
import { REEL_FILLER_SYMBOL_IDS } from "../assets/symbols/symbolCatalog";
import { vsBattleFeature } from "../features/vs-battle/VsBattleFeature";
import { VsWildLayer } from "../features/vs-battle/VsWildLayer";
import { VsWildView } from "../features/vs-battle/VsWildView";
import { VS_WILD_TUNING } from "../tuning/vsWildTuning";
import { savedTotalFeature } from "../features/saved-total/SavedTotalFeature";
import { SAVED_TOTAL_TUNING } from "../tuning/savedTotalTuning";
import {
  BrightnessSymbolLandEffect,
  CompositeSymbolLandEffect,
  CompositeSymbolWinEffect,
  DelimitedGridCodec,
  DropThud,
  GridLayer,
  LandBounce,
  OverlayFadeSymbolWinEffect,
  PitchedLandSound,
  RotateSymbolWinEffect,
  ScalePulseSymbolWinEffect,
  ScatterHighlightEffect,
  SquashSymbolLandEffect,
  SymbolLandSounds,
  SymbolWinBrightnessEffect,
  WinHighlightEffect,
} from "@slot/presentation";
import { CompositeGridPresenter } from "@slot/mechanics-core";
import { BootGridPresenter, WinSequencePresenter, createGridAudio, createShellRoundPlugins } from "@slot/game-shell";
import type { GamePresentation, PresentationDeps } from "@slot/game-shell";

/** Symbol cell size in design units (the scene's `ReelGridView` cell). */
const GRID_CELL_SIZE = 120;

/** The win cue per win in a spin, wrapping: the audio mapping gives them the three pitches of the win sound. */
const WIN_CUE_EVENTS = ["grid.win_highlight", "grid.win_highlight.2", "grid.win_highlight.3"] as const;

/**
 * This game's round presentation (plan §2.2): the grid and its mechanic, the symbol effects, the shell's win sequence
 * and the plugin list — everything the shell builds the engine, the runtime and the control bar around (decision 46:
 * the shared behaviour is the engine's, this file names this game's pieces).
 *
 * Mechanic: plain reels (GridLayer + ReelSpinMechanic + ReelGridPresenter + buildReelsReplayPlugin). The symbols are
 * sprites: a win dims the grid, pulses its winners (the L symbols wobble too, the H symbols switch to their alt frame
 * and keep it) and shows its amount with the flare label; the win stays on the grid until the next spin. A VS battle
 * plays on a board over its reel, which stays until the next spin scrolls it out with the reel. The Super Bonus keeps
 * its saved total on a panel over the grid.
 */
export function createPresentation(scene: BloodyTeddyScene, deps: PresentationDeps): GamePresentation {
  const { game, gameModals } = scene;
  const s = deps.services;
  const log = s.log;
  const runner = s.presentationTaskRunner;

  // One exposure per cell, the dim's and the winners' shine (a winner flows from the dim into its shine), on the cell's
  // content node so the H symbols' alt frame darkens and shines with its sprite; released at each bind.
  const exposure = new SymbolWinBrightnessEffect({ taskRunner: runner, tuning: WIN_PRESENTATION_TUNING.brightness, once: true, dimNodeResolver: (sprite) => sprite.parent });

  const anticipationBg = game.anticipationBackground;
  anticipationBg.setTuning(ANTICIPATION_BACKGROUND_TUNING);

  const gridAudio = createGridAudio({ audioService: s.audioService, runner });

  // ── The grid and its mechanic: the layer owns cells, pool and layout; the reels move symbols on it ─────────────
  const gridLayer = new GridLayer(
    {
      gridRoot: game.gridContainer.gridRoot,
      layoutRoot: game.gridContainer.gridArea,
      layoutArea: GRID_AREA_SIZE,
      gridColumnCount: deps.grid.columns,
      gridRowCount: deps.grid.rows,
      cellWidth: GRID_CELL_SIZE,
      cellHeight: GRID_CELL_SIZE,
      autoFitToLayoutRoot: true,
      centerGrid: true,
      // Five evenly spaced reels (the layout's default offsets are the 6-column padded set).
      columnOffsets: [0, 0, 0, 0, 0],
    },
    {
      definitionResolver: game.symbolCatalog,
      log,
      // Landing reactions, in play order: the thud, the pitched scatter stinger, the other symbols' sounds (the VS's),
      // then the grid bounce on a scatter (this game's rules; the shell only dispatches).
      landReactions: [
        new DropThud({ audio: gridAudio }),
        new PitchedLandSound({ audio: gridAudio, soundId: "grid_scatter_drop", nowMs: () => runner.engineSeconds * 1000 }),
        new SymbolLandSounds({ audio: gridAudio, ownedElsewhere: new Set(["grid_scatter_drop"]) }),
        new LandBounce({ target: game.gridContainer.gridArea, runner, triggerSymbolIds: new Set(["FS"]), tuning: LAND_BOUNCE_TUNING }),
      ],
      // A bound cell starts undimmed: a dimmed symbol that scrolled out comes back clean.
      onSymbolBound: (view) => exposure.release(view.symbolSprite),
    },
  );

  // The land looks: the scatter squashes, the scatter and the VS shine.
  const landEffect = new CompositeSymbolLandEffect([
    new SquashSymbolLandEffect({ targetSymbolIds: new Set(SQUASH_LAND_SYMBOL_IDS), tuning: SQUASH_LAND_TUNING, taskRunner: runner }),
    new BrightnessSymbolLandEffect({ targetSymbolIds: new Set(SHINE_LAND_SYMBOL_IDS), tuning: LAND_SHINE_TUNING, taskRunner: runner }),
  ]);

  const reel = new ReelSpinMechanic(gridLayer, {
    taskRunner: runner,
    audio: gridAudio,
    motionTuning: REEL_MOTION_TUNING,
    // Filler-symbol motion blur via pre-blurred atlas frames (the drum's filler scrolls blurred).
    fillerBlurDefinitions: game.symbolCatalog.fillerBlurDefinitions().size > 0 ? game.symbolCatalog.fillerBlurDefinitions() : null,
    fillerSymbolIds: REEL_FILLER_SYMBOL_IDS,
    speedModeProvider: () => s.presentationClock.getMode(),
    anticipationFrame: game.createAnticipationFrame ? { create: game.createAnticipationFrame, parent: game.gridContainer.gridOverlay } : null,
    anticipationSessionListener: {
      onStart: (ordinal) => {
        anticipationBg.noteReelStart(ordinal);
        if (ordinal === 1) s.audioService.control.duckMusic(ANTICIPATION_BACKGROUND_TUNING.musicDuckScale);
      },
      onEnd: () => {
        anticipationBg.rampOut();
        s.audioService.control.unduckMusic();
      },
    },
    landEffect,
  });

  // ── Round presentation, in construction order: presenters and effects → the win sequence → the plugin list ─────
  const gridCodec = new DelimitedGridCodec(deps.grid.columns, deps.grid.rows);
  const basePresenter = new ReelGridPresenter({ reel, gridCodec, presentationBarrier: s.presentationBarrier, log });

  // The winners' animation, once per win: the H symbols switch to their alt frame and KEEP it until the next spin, every
  // winner shines and pulses (the L tier on both axes), and the L symbols wobble.
  const lSymbolIds = new Set(SYMBOL_WIN_ROTATE_SYMBOL_IDS);
  const symbolWinFrames = resolveSymbolWinFrames(scene.symbolsAtlas);
  const symbolWinEffect = new CompositeSymbolWinEffect([
    ...(symbolWinFrames.size > 0
      ? [
          new OverlayFadeSymbolWinEffect({
            framesBySymbol: symbolWinFrames,
            taskRunner: runner,
            fadeInSeconds: WIN_PRESENTATION_TUNING.symbolFrames.fadeInSeconds,
            startDelayMilliseconds: WIN_PRESENTATION_TUNING.symbolFrames.startDelayMilliseconds,
            baseFadeOutDelaySeconds: WIN_PRESENTATION_TUNING.symbolFrames.baseFadeOutDelaySeconds,
            hold: true,
            markDirtyOnHold: true,
          }),
        ]
      : []),
    exposure,
    new ScalePulseSymbolWinEffect((symbolId) => (lSymbolIds.has(symbolId) ? WIN_PRESENTATION_TUNING.scalePulseL : WIN_PRESENTATION_TUNING.scalePulse), runner, { once: true }),
    new RotateSymbolWinEffect(WIN_PRESENTATION_TUNING.rotate, runner, { symbolIds: lSymbolIds, once: true }),
  ]);

  // The payline needle through each win's cells, under the symbols (the grid's FX underlay, as in the Cocos scene); a
  // group's lines draw as one stroke. Drawn on the highlight's beat, from its per-line hook.
  const needle = scene.assets.sheets.needleLine;
  const paylines = new ReelPaylineEffect({
    grid: gridLayer,
    parent: game.gridContainer.fxUnderlays,
    art: needle?.textures["mid"] ? { start: needle.textures["start"] ?? null, mid: needle.textures["mid"], end: needle.textures["end"] ?? null } : null,
    tuning: REEL_WIN_TUNING.payline,
    taskRunner: runner,
    log,
  });

  const winHighlightEffect = new WinHighlightEffect({
    gridView: gridLayer,
    log,
    moneyFormatter: s.moneyContext.formatter,
    createWinAmountLabel: game.createWinAmountLabel,
    overlayRoot: game.fxOverlays,
    timings: WIN_PRESENTATION_TUNING.layer,
    labelTuning: WIN_PRESENTATION_TUNING.label,
    symbolWinEffect,
    exposureOwner: exposure,
    taskRunner: runner,
    onLineHighlighted: (positionMask, _winAmount, scopeId, lineMasks) => paylines.drawLines(lineMasks ?? [positionMask], scopeId),
  });

  const scatterHighlightEffect = new ScatterHighlightEffect({ gridView: gridLayer, taskRunner: runner, log });

  // The win sequence (the shell's): the beat before each win, the cue climbing three pitches across a spin's wins, the
  // haptic, the per-win slam, and the idle cycle that re-shows the spin's lines until the next spin. A win shows its
  // amount: a multiplied line's "base × mult" is the cycle's.
  const winSequencePresenter = new WinSequencePresenter({
    runner,
    audioService: s.audioService,
    hapticsService: s.hapticsService,
    winHighlightEffect,
    tuning: WIN_PRESENTATION_TUNING,
    showMultiplierBreakdown: false,
    winCueEvents: WIN_CUE_EVENTS,
    cycle: WIN_PRESENTATION_TUNING.cycle,
    log,
  });

  // The Super Bonus saved total: its panel over the grid, raised and collected by the server's actions.
  const savedTotal = savedTotalFeature.createPresenter({ view: game.savedTotal, runner, tuning: SAVED_TOTAL_TUNING, log });

  // The VS battles' boards over their reels (above the symbols, beside the grid's clip, as in the Cocos scene). In the
  // Super Bonus a battle's top value is the saved total, which flies in from the panel to become its label.
  const { assets } = scene;
  const vsWilds = new VsWildLayer({
    grid: gridLayer,
    parent: game.gridContainer.gridOverlay,
    createView: () => new VsWildView({ skeletonData: assets.skeletons.vsWild, art: assets.sheets.vsWildArt, glowArt: assets.sheets.vsWildFrame, spineDriver: deps.spineDriver, log }),
    runner,
    tuning: VS_WILD_TUNING,
    topLabelEntrance: (entrance) => savedTotal.flyIntoBattle(entrance),
    log,
  });

  const gridPresenter = new CompositeGridPresenter({ base: basePresenter, effects: [vsWilds, winHighlightEffect, paylines] });

  /**
   * Per-SPIN teardown + scroll start — runs before EVERY reel spin (the click and every spin boundary). The idle cycle
   * stops first (the click's pre-spin comes before the round's first boundary). The previous spin's boards are taken
   * before the teardown, so they ride the drum out with their reels instead of vanishing; the drum moves them from the
   * frame the scroll starts, hence right after it.
   */
  const beginReelSpinCycle = (): void => {
    winSequencePresenter.stopWinCycle();
    const leaving = vsWilds.takeActiveWilds();
    gridPresenter.clearForNewSpin();
    reel.startContinuousSpin();
    for (const wild of leaving) {
      void reel.scrollOverlayOutWithColumn(wild.column, wild.node).then(() => {
        if (!wild.node.destroyed) wild.node.destroy({ children: true });
      });
    }
  };

  const bootGridPresenter = new BootGridPresenter({
    // A repaint (boot, restore, recovery) starts from a bare grid: no board from an earlier round stays over it.
    grid: {
      reset: () => {
        vsWilds.clearForNewSpin();
        reel.resetPresentationState();
      },
      paint: (encoded) => basePresenter.paintGrid(encoded),
    },
    waitForNextRenderedFrame: deps.waitForNextRenderedFrame,
    log,
  });

  const mechanic = buildReelsReplayPlugin({ gridPresenter, basePresenter });

  // The game's features: the VS battle (its stinger, the bear's reaction, the reel's battle) and the saved total.
  const playFeatureSound = (soundId: string): void => {
    s.audioService.presentation.playSoundId?.(soundId);
  };
  const vsBattle = vsBattleFeature.createPresenter({
    wilds: vsWilds,
    reactCharacter: game.characterView ? () => game.characterView?.react() : null,
    playSound: playFeatureSound,
  });

  // ── The replay plugins: the shell's half of the round in its one order (its overlay presenters over this game's
  // panels), with the mechanic and this game's features in their slots (decision 46) ─────────────────────────────
  const plugins = createShellRoundPlugins({
    services: s,
    lifecycle: deps.lifecycle,
    panels: {
      featureTrigger: gameModals.featureTriggerView,
      featureFinished: gameModals.featureFinishedView,
      bigWin: gameModals.bigWinView,
      gameTransition: gameModals.gameTransitionView,
    },
    tuning: { featureTrigger: FEATURE_TRIGGER_TUNING, featureFinished: FEATURE_FINISHED_TUNING, bigWin: BIG_WIN_TUNING, autoAdvance: WIN_PRESENTATION_TUNING.autoAdvance },
    winSequence: winSequencePresenter,
    scatterHighlightEffect,
    beginSpinCycle: beginReelSpinCycle,
    // Each spin's first battle makes the bear react again.
    onSpinStart: () => game.characterView?.resetReactionState(),
    // At a feature's exit, before its finished panel, the last free spin's win, strokes and battle boards go: the base
    // game comes back to a plain grid, with no win cycling and no multiplier showing that applies to nothing (D3).
    clearStandingWin: () => {
      winHighlightEffect.clearStandingWin();
      paylines.clearForNewSpin();
      vsWilds.clearForNewSpin();
    },
    mechanic: mechanic.plugin,
    features: [vsBattleFeature.createPlugin(vsBattle), savedTotalFeature.createPlugin(savedTotal)],
  });

  return {
    framePresenter: mechanic.framePresenter,
    plugins,
    bootGridPresenter,
    // Spin-on-click (pre-response): the per-spin teardown + the reel scroll, then release the "spin" barrier.
    preSpin: async () => {
      beginReelSpinCycle();
      s.presentationBarrier.release("spin");
    },
    // Manual slam: the reels brake all columns together under their own scope; the same tap snaps the win line
    // presenting now, or the battle playing now (each acts only in its own phase).
    slam: () => {
      const reelSlammed = reel.requestSlamStop();
      const winSnapped = winSequencePresenter.slamActiveWin();
      const battleSnapped = vsWilds.slamActiveWild();
      return reelSlammed || winSnapped || battleSnapped;
    },
    slamScopes: mechanic.slamScopes,
    // The backend runs no gamble (its routes are not mounted).
    gamblePresentation: null,
  };
}
